import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'

import { Layout } from '../components/Layout'
import { components } from '../slices'
import SliceZoneComponent from '../components/SliceZone'
import { getDefaultPageData } from '../utils/getDefaultPageData'
import AuthorPageHeader from '../components/author/AuthorPageHeader'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateAuthorBreadcrumbsData } from '../utils/generateAuthorBreadcrumbsData'
import { SEO } from '../components/Head'

const TYPE_KEY = `${process.env.GATSBY_STORAGE_KEY}-post-type`
const ARTICLE_KEY = `${process.env.GATSBY_STORAGE_KEY}-post-article`

const AuthorTemplate = (props) => {
  const { data } = props

  if (!data) return null

  const headerArticlesData = data.headerArticles.nodes

  useEffect(() => {
    const { state } = props.location
    if (state) {
      sessionStorage.setItem(TYPE_KEY, JSON.stringify(state?.type))
      sessionStorage.setItem(ARTICLE_KEY, JSON.stringify(state?.article))
    }

    return () => {
      sessionStorage.removeItem(TYPE_KEY)
      sessionStorage.removeItem(ARTICLE_KEY)
    }
  }, [])

  const { topLine, footer, header, page, activeDoc, topMenu } =
    getDefaultPageData(data, 'prismicAuthorPage')

  const articles = data.allPrismicArticlePage.nodes
  const author = page.author.document.data
  const { state } = props.location
  const article =
    (typeof sessionStorage === 'object' &&
      sessionStorage?.getItem(ARTICLE_KEY)) ||
    JSON.stringify(state?.article)
  const type =
    (typeof sessionStorage === 'object' && sessionStorage?.getItem(TYPE_KEY)) ||
    JSON.stringify(state?.type)

  return (
    <Layout
      topMenu={topMenu.data}
      footer={footer.data}
      topLine={topLine.data}
      header={{ nav: header.data.nav, articles: header.data.articles }}
      activeDocMeta={activeDoc}
      headerArticlesData={headerArticlesData}
    >
      <div className="author-page">
        <div className="container">
          {article && type ? (
            <Breadcrumbs
              data={generateAuthorBreadcrumbsData({ article, type, author })}
            />
          ) : null}
          <AuthorPageHeader data={author} articleCount={articles.length} />
        </div>
        <SliceZoneComponent
          context={{
            articles: articles,
          }}
          slices={page.body}
          components={components}
        />
      </div>
    </Layout>
  )
}

export const Head = ({ data }) => {
  const { page, pageContent } = getDefaultPageData(data, 'prismicAuthorPage')
  return (
    <SEO
      meta={{
        title: page?.meta_title,
        description: page?.meta_description,
        image: page?.meta_image,
        type: page?.type,
        pageContent,
      }}
    />
  )
}

export const query = graphql`
  query authorQuery($id: String, $lang: String, $uid: String) {
    prismicAuthorPage(id: { eq: $id }, lang: { eq: $lang }) {
      _previewable
      url
      uid
      type
      id
      lang
      alternate_languages {
        id
        type
        lang
        uid
      }
      data {
        meta_title {
          text
        }
        meta_image {
          url
          alt
        }
        meta_description {
          text
        }
        author {
          document {
            ... on PrismicPerson {
              id
              data {
                biography {
                  text
                }
                name
                photo {
                  gatsbyImageData
                  alt
                }
                position
                socials {
                  social
                  social_icon {
                    gatsbyImageData
                    alt
                  }
                  social_link {
                    url
                    target
                  }
                }
              }
            }
          }
        }
        body {
          ... on PrismicSlice {
            id
            slice_label
            slice_type
            ...AuthorPageDataBodyAuthorPostListSection
            ...AuthorPageDataBodyJoinUsSection
            ...AuthorPageDataBodyCallToAction
            ...AuthorPageDataBodyBackgroundSection
          }
        }
      }
    }
    prismicTopLine(lang: { eq: $lang }) {
      ...TopLineFragment
    }
    prismicHeader(lang: { eq: $lang }) {
      data {
        articles {
          article_link {
            document {
              ... on PrismicArticlePage {
                ...ArticlePage
              }
            }
          }
        }
        nav {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...HeaderDataNavFooter
          ...HeaderDataNavButton
          ...HeaderDataNavNavItem
        }
      }
    }
    prismicFooter(lang: { eq: $lang }) {
      data {
        body {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBodyCopyright
          ...FooterDataBodyFooter
          ...FooterDataBodySocials
          ...FooterDataBodyAwards
          ...FooterDataBodyForm
        }
        body1 {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...FooterDataBody1NavItem
        }
      }
    }
    allPrismicArticlePage(
      filter: {
        lang: { eq: $lang }
        data: { invisible: { eq: "no" }, article_author: { uid: { eq: $uid } } }
      }
      sort: { first_publication_date: DESC }
    ) {
      nodes {
        type
        uid
        lang
        id
        data {
          title {
            text
          }
          article_categories {
            article_category
          }
          short_description
          publish_date(formatString: "MMM DD,YYYY")
          invisible
          article_author {
            document {
              ... on PrismicPerson {
                id
                data {
                  name
                  photo {
                    gatsbyImageData
                    alt
                  }
                }
              }
            }
          }
          image {
            gatsbyImageData
            alt
          }
          type
        }
      }
    }
    headerArticles: allPrismicArticlePage(
      filter: {
        lang: { eq: $lang }
        data: { invisible: { eq: "no" }, type: { eq: "blog" } }
      }
      limit: 2
      sort: { first_publication_date: DESC }
    ) {
      nodes {
        ...ArticlePage
      }
    }
  }
`

export default withPrismicPreview(AuthorTemplate)
